import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, ArrowUp, Download, Table2 } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import type { AllowlistedTable, ExploreColumn } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { EmptyState } from "@/components/EmptyState";
import { SkeletonLines } from "@/components/Skeleton";
import { useExplore } from "@/hooks/queries";

type Cell = unknown;
type Row = Record<string, Cell>;

// Render any BigQuery cell value as readable text without ever throwing. Objects/arrays (BQ
// STRUCT/ARRAY) are JSON-stringified; null/undefined become an em dash.
const renderCell = (value: Cell): string => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

// A field needs CSV quoting if it contains a comma, quote, or newline; quotes inside double up.
const CSV_NEEDS_QUOTE = /[",\n]/;
const CSV_QUOTE = /"/g;

// Escape one CSV field per RFC 4180.
const csvField = (value: Cell): string => {
  if (value === null || value === undefined) {
    return "";
  }
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return CSV_NEEDS_QUOTE.test(text) ? `"${text.replace(CSV_QUOTE, '""')}"` : text;
};

const downloadCsv = (
  table: string,
  columns: readonly ExploreColumn[],
  rows: readonly Row[],
): void => {
  const header = columns.map((c) => csvField(c.name)).join(",");
  const body = rows.map((row) => columns.map((c) => csvField(row[c.name])).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}\n`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${table}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const ROW_HEIGHT = 34;
const VIEWPORT_HEIGHT = 460;
const OVERSCAN = 12;
const DISABLED_OPACITY = 0.5;

interface ExploreGridProps {
  readonly columns: readonly ExploreColumn[];
  readonly rows: readonly Row[];
}

// Virtualized TanStack Table (sortable, read-only). Only the visible rows are mounted, so a
// LIMIT-capped preview of a wide staging table stays smooth.
const DsExploreGrid = ({ columns, rows }: ExploreGridProps): ReactNode => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const columnDefs = useMemo<ColumnDef<Row>[]>(
    () =>
      columns.map((col) => ({
        id: col.name,
        accessorFn: (row) => row[col.name],
        header: col.name,
        cell: (info) => renderCell(info.getValue()),
        sortUndefined: "last",
      })),
    [columns],
  );

  const table = useReactTable({
    data: rows as Row[],
    columns: columnDefs,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tableRows = table.getRowModel().rows;
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0) : 0;

  return (
    <div
      ref={scrollRef}
      style={{
        height: VIEWPORT_HEIGHT,
        overflow: "auto",
        border: "1px solid var(--br-border)",
        borderRadius: "var(--br-radius-md)",
      }}
    >
      <table className="data-table" style={{ width: "100%" }}>
        <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "var(--br-surface-2)" }}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                return (
                  <th key={header.id}>
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: 0,
                        font: "inherit",
                        color: sorted ? "var(--br-text-primary)" : "inherit",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sorted === "asc" ? <ArrowUp size={11} aria-hidden /> : null}
                      {sorted === "desc" ? <ArrowDown size={11} aria-hidden /> : null}
                    </button>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ height: paddingTop, padding: 0, border: "none" }}
              />
            </tr>
          ) : null}
          {virtualRows.map((virtualRow) => {
            const row = tableRows[virtualRow.index];
            if (!row) {
              return null;
            }
            return (
              <tr key={row.id} style={{ height: ROW_HEIGHT }}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="mono"
                    style={{
                      maxWidth: 320,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={renderCell(cell.getValue())}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
          {paddingBottom > 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ height: paddingBottom, padding: 0, border: "none" }}
              />
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

// A request to focus a table, from elsewhere on the page (a pipeline row or an ERD node). The
// `nonce` makes each click a distinct object identity, so re-clicking the SAME table still re-runs
// the focus effect (and re-scrolls) — a plain string couldn't express "focus this again".
export interface DsFocusRequest {
  readonly table: string;
  readonly nonce: number;
}

export interface DsRawExplorerProps {
  readonly tables: readonly AllowlistedTable[];
  readonly tablesLoading?: boolean;
  readonly tablesError?: boolean;
  // The table to focus (bare name, e.g. "stg_e2_orders"), resolved against the allowlist and
  // ignored if not explorable. Null when nothing has been requested.
  readonly focusRequest?: DsFocusRequest | null;
}

// The bare table name from a fully-qualified id like "`busright-stg.supply_chain.stg_e2_orders`":
// strip BigQuery backticks and take the segment after the last dot.
const bareTableName = (fqTable: string): string => {
  const unquoted = fqTable.replace(/`/g, "");
  const lastDot = unquoted.lastIndexOf(".");
  return lastDot >= 0 ? unquoted.slice(lastDot + 1) : unquoted;
};

// Resolve a bare table name to an allowlist KEY (the value the picker / explore endpoint expects).
// The allowlist key is a short slug ("orders"), so match against the fqTable's bare name (the value
// a pipeline surface / ERD node uses), then fall back to key/label. Returns "" when not explorable.
const resolveAllowlistKey = (tables: readonly AllowlistedTable[], table: string): string => {
  const match = tables.find(
    (t) => bareTableName(t.fqTable) === table || t.key === table || t.label === table,
  );
  return match?.key ?? "";
};

// Section 3 — BigQuery-preview-style explorer. The picker is rendered ONLY from the server
// allowlist, so a non-allowlisted table can never be requested. Selecting a table fetches a
// LIMIT-capped preview via useExplore; rows export to CSV client-side. Read-only throughout.
export const DsRawExplorer = ({
  tables,
  tablesLoading,
  tablesError,
  focusRequest,
}: DsRawExplorerProps): ReactNode => {
  const [selected, setSelected] = useState("");
  const { data, isLoading, isError, error } = useExplore(selected);
  const payload = data?.data;
  const rows = (payload?.rows ?? []) as readonly Row[];
  const cardRef = useRef<HTMLDivElement>(null);

  // When the page asks us to focus a table (and the allowlist has loaded), resolve it and select it,
  // then scroll the explorer into view. Re-runs whenever a new focusRequest object arrives.
  useEffect(() => {
    if (!focusRequest || tables.length === 0) {
      return;
    }
    const key = resolveAllowlistKey(tables, focusRequest.table);
    if (key === "") {
      return;
    }
    setSelected(key);
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusRequest, tables]);

  const selectedLabel = tables.find((t) => t.key === selected)?.label ?? selected;

  const picker = (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
      <label htmlFor="ds-table-picker" style={{ fontSize: 12, color: "var(--br-text-secondary)" }}>
        Table
      </label>
      <select
        id="ds-table-picker"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={tablesLoading || tablesError || tables.length === 0}
        style={{
          padding: "6px 10px",
          fontSize: 13,
          color: "var(--br-text-primary)",
          background: "var(--br-surface-2)",
          border: "1px solid var(--br-border)",
          borderRadius: "var(--br-radius-sm)",
        }}
      >
        <option value="">Select an allow-listed table…</option>
        {tables.map((t) => (
          <option key={t.key} value={t.key}>
            {t.label}
          </option>
        ))}
      </select>
      {selected && payload ? (
        <button
          type="button"
          onClick={() => downloadCsv(selected, payload.columns, rows)}
          disabled={rows.length === 0}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 600,
            color: "var(--br-accent-contrast)",
            background: "var(--br-accent)",
            border: "none",
            borderRadius: "var(--br-radius-sm)",
            cursor: rows.length === 0 ? "not-allowed" : "pointer",
            opacity: rows.length === 0 ? DISABLED_OPACITY : 1,
          }}
        >
          <Download size={13} aria-hidden />
          Export CSV
        </button>
      ) : null}
    </div>
  );

  const renderBody = (): ReactNode => {
    if (tablesError) {
      return (
        <EmptyState
          title="Allowlist unavailable"
          message="Could not load the explorable-table allowlist from the API."
        />
      );
    }
    if (!selected) {
      return (
        <EmptyState
          icon={Table2}
          title="Pick a table to preview"
          message="Only server-allow-listed staging tables are available. Previews are read-only and row-capped."
        />
      );
    }
    if (isLoading) {
      return <SkeletonLines count={8} />;
    }
    if (isError) {
      return (
        <EmptyState
          title="Could not load preview"
          message={error instanceof Error ? error.message : "Unknown error."}
        />
      );
    }
    if (!payload || payload.columns.length === 0) {
      return <EmptyState title="No columns" message="The API returned no schema for this table." />;
    }
    return (
      <>
        <div style={{ marginBottom: 10, fontSize: 12, color: "var(--br-text-muted)" }}>
          <span className="br-tabular-nums">{rows.length}</span> rows ·{" "}
          <span className="br-tabular-nums">{payload.columns.length}</span> columns · capped at{" "}
          <span className="br-tabular-nums">{payload.rowLimit}</span>
        </div>
        {rows.length === 0 ? (
          <EmptyState
            title="No rows"
            message="This table returned no rows within the preview cap."
          />
        ) : (
          <DsExploreGrid columns={payload.columns} rows={rows} />
        )}
      </>
    );
  };

  return (
    <div ref={cardRef} style={{ scrollMarginTop: 16 }}>
      <ChartCard
        title="Raw explorer"
        subtitle="Read-only, allow-listed staging-table preview"
        source={selected ? selectedLabel : undefined}
        action={picker}
      >
        {renderBody()}
      </ChartCard>
    </div>
  );
};
